/**
 * HT Cart Validator - Clean Version
 * Security validation for calculator ecosystem
 */

'use strict';

class HTCartValidator {
  constructor() {
    this.calculatorType = 'regular';
    this.sessionToken = this.generateSessionToken();
    
    // Validation bounds
    this.QUANTITY_MIN = 1;
    this.QUANTITY_MAX = 10000;
    this.PRICE_MIN_PENCE = 1;
    this.PRICE_MAX_PENCE = 1000000;
    
    console.log('🛡️ HT Cart Validator initialized');
  }

  generateSessionToken() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    return btoa(`${timestamp}-${random}`).substring(0, 32);
  }

  validateCartData(calculatorData = null, clientPrice = null, sessionToken = null) {
    console.log('🔍 Starting cart validation');
    
    const result = {
      valid: false,
      errors: [],
      sanitizedData: {},
      securityFlags: [],
      calculatedPrice: 0,
      calculatorType: this.calculatorType
    };

    try {
      // Use provided data or extract from calculator
      if (!calculatorData) {
        calculatorData = this.extractCalculatorData();
      }
      
      if (clientPrice === null) {
        clientPrice = this.extractClientPrice();
      }

      // Sanitize inputs
      result.sanitizedData = this.sanitizeInputs(calculatorData);

      // Validate required fields
      const fieldValidation = this.validateRequiredFields(result.sanitizedData);
      if (!fieldValidation.valid) {
        result.errors.push(...fieldValidation.errors);
      }

      // Validate quantity
      const quantityValidation = this.validateQuantity(result.sanitizedData.quantity);
      if (!quantityValidation.valid) {
        result.errors.push(...quantityValidation.errors);
      }

      // Validate price reasonableness
      if (clientPrice !== null) {
        const priceValidation = this.validatePriceReasonableness(clientPrice);
        if (!priceValidation.valid) {
          result.errors.push(...priceValidation.errors);
        }
        result.calculatedPrice = clientPrice;
      }

      // Set overall result
      result.valid = result.errors.length === 0;

      console.log(result.valid ? '✅' : '❌', 'Cart validation complete:', result);
      return result;

    } catch (error) {
      console.error('💥 Cart validation error:', error);
      result.errors.push('Validation system error');
      return result;
    }
  }

  extractCalculatorData() {
    const data = {};
    
    // Try to extract from active calculator
    if (window.htCalculatorStyled) {
      data.material = document.getElementById('ht-material-select')?.value || '';
      data.style_template = document.getElementById('ht-shape-select')?.value || '';
      data.quantity = document.getElementById('ht-quantity-select')?.value || '';
      data.print_sides = document.getElementById('ht-print-sides-select')?.value || '';
      data.print_ink = document.getElementById('ht-print-ink-select')?.value || '';
    } else if (window.htCalculator) {
      data.material = document.getElementById('ht-material-select')?.value || '';
      data.shape = document.getElementById('ht-shape-select')?.value || '';
      data.size = document.getElementById('ht-size-select')?.value || '';
      data.quantity = document.getElementById('ht-quantity-select')?.value || '';
      data.print_sides = document.getElementById('ht-print-sides-select')?.value || '';
      data.print_ink = document.getElementById('ht-print-ink-select')?.value || '';
    }
    
    return data;
  }

  extractClientPrice() {
    const priceElements = [
      document.querySelector('.ht-wizard-price .price'),
      document.getElementById('ht-calculator-total-price')
    ];
    
    for (const element of priceElements) {
      if (element && element.textContent) {
        const priceText = element.textContent.trim();
        const cleanPrice = priceText.replace(/[£,\s]/g, '');
        const priceValue = parseFloat(cleanPrice);
        
        if (!isNaN(priceValue) && priceValue > 0) {
          return Math.round(priceValue * 100); // Convert to pence
        }
      }
    }
    
    return 0;
  }

  sanitizeInputs(data) {
    const sanitized = {};
    
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        sanitized[key] = value
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#x27;')
          .trim();
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }

  validateRequiredFields(data) {
    const result = { valid: true, errors: [] };

    if (!data.quantity || data.quantity === '' || data.quantity === 0) {
      result.valid = false;
      result.errors.push('Quantity is required');
    }

    if (!data.material || data.material === '') {
      result.valid = false;
      result.errors.push('Material is required');
    }

    // Check for style template or shape depending on calculator type
    if (!data.style_template && !data.shape) {
      result.valid = false;
      result.errors.push('Style selection is required');
    }

    return result;
  }

  validateQuantity(quantity) {
    const result = { valid: true, errors: [] };
    const qty = parseInt(quantity) || 0;
    
    if (qty < this.QUANTITY_MIN) {
      result.valid = false;
      result.errors.push(`Quantity must be at least ${this.QUANTITY_MIN}`);
    }
    
    if (qty > this.QUANTITY_MAX) {
      result.valid = false;
      result.errors.push(`Quantity cannot exceed ${this.QUANTITY_MAX}`);
    }
    
    return result;
  }

  validatePriceReasonableness(price) {
    const result = { valid: true, errors: [] };
    
    if (price < this.PRICE_MIN_PENCE) {
      result.valid = false;
      result.errors.push('Price too low');
    }
    
    if (price > this.PRICE_MAX_PENCE) {
      result.valid = false;
      result.errors.push('Price too high');
    }
    
    if (isNaN(price)) {
      result.valid = false;
      result.errors.push('Invalid price value');
    }
    
    return result;
  }

  getSessionToken() {
    return this.sessionToken;
  }
}

// Create validator instance immediately
window.htCartValidator = new HTCartValidator();
window.HTCartValidator = HTCartValidator;

console.log('✅ HT Cart Validator loaded successfully');